/**
 * Print-window attachment selection: which of the record's files should be
 * merged into the printed PDF (PDF/image) or attached to email only
 * (everything else). Resolves obj_type once per mount via configObjectApi —
 * the print window doesn't otherwise load the object's config — then reuses
 * the existing, already-permission-checked GET /v1/attachments/list.
 * Link-type attachments (URL only, no file bytes) are excluded — there is
 * nothing to merge or attach.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiService } from '@/utils/api'
import { configObjectApi } from '../../designer/services/configObjectApi'
import type { Attachment } from '../AttachmentPanel'

export type UsePrintAttachmentsArgs = {
  connectionId: string | undefined
  tableName: string | undefined
  instanceId: string | undefined
}

export function isMergeableAttachment(contentType: string): boolean {
  return contentType === 'application/pdf' || contentType.startsWith('image/')
}

function isLinkAttachment(att: Attachment): boolean {
  return Boolean(att.description && /^https?:\/\//.test(att.description))
}

export function usePrintAttachments({
  connectionId,
  tableName,
  instanceId,
}: UsePrintAttachmentsArgs) {
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!connectionId || !tableName || !instanceId) {
      setAttachments([])
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const cfg = await configObjectApi.getObject(connectionId, tableName)
        const ownerType = (cfg as { obj_type: string }).obj_type
        const qs =
          `connection_id=${encodeURIComponent(connectionId)}` +
          `&owner_type=${encodeURIComponent(ownerType)}` +
          `&owner_name=${encodeURIComponent(tableName)}` +
          `&owner_id=${encodeURIComponent(instanceId)}`
        const data = await ApiService.get<Attachment[]>(`/v1/attachments/list?${qs}`)
        if (!cancelled) setAttachments(data.filter((a) => !isLinkAttachment(a)))
      } catch (e) {
        if (!cancelled) {
          setAttachments([])
          setError(e instanceof Error ? e.message : 'Failed to load attachments')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [connectionId, tableName, instanceId])

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectedAttachments = useMemo(
    () => attachments.filter((a) => selected.has(a.id)),
    [attachments, selected],
  )
  const mergeableSelected = useMemo(
    () => selectedAttachments.filter((a) => isMergeableAttachment(a.content_type)),
    [selectedAttachments],
  )
  const otherSelected = useMemo(
    () => selectedAttachments.filter((a) => !isMergeableAttachment(a.content_type)),
    [selectedAttachments],
  )

  return {
    attachments,
    loading,
    error,
    selected,
    toggle,
    selectedAttachments,
    mergeableSelected,
    otherSelected,
  }
}
